import { DOCUMENT } from '@angular/common';
import { type HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

/** Same key as Axelor cookie name — used for sessionStorage mirror. */
export const CSRF_SESSION_STORAGE_KEY = 'CSRF-TOKEN';

export const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * CSRF token: in-memory + sessionStorage mirror, optional cookie read (same-origin).
 * Token is filled from API response headers (`X-CSRF-Token`), not via a separate `ws/app/info` call —
 * that endpoint returns 302 → `login.jsp` when there is no session, which is wrong for the SPA shell.
 */
@Injectable({ providedIn: 'root' })
export class CsrfTokenService {
  private readonly document = inject(DOCUMENT);

  private readonly tokenState = signal<string | null>(this.readInitialFromSessionStorage());

  public readonly token = this.tokenState.asReadonly();

  public getToken(): string | null {
    const fromSignal = this.tokenState();
    if (fromSignal) {
      return fromSignal;
    }

    const fromStorage = this.readSessionStorage();
    if (fromStorage) {
      this.tokenState.set(fromStorage);
      return fromStorage;
    }

    return this.readCookieToken();
  }

  public setToken(token: string): void {
    const normalized = token.trim();
    if (!normalized) {
      return;
    }
    this.tokenState.set(normalized);
    this.writeSessionStorage(normalized);
  }

  public applyFromHeaders(headers: HttpHeaders): void {
    const next =
      headers.get(CSRF_HEADER_NAME) ?? headers.get(CSRF_HEADER_NAME.toLowerCase()) ?? null;
    if (next) {
      this.setToken(next);
    }
  }

  public clear(): void {
    this.tokenState.set(null);
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem(CSRF_SESSION_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }

  private readInitialFromSessionStorage(): string | null {
    return this.readSessionStorage();
  }

  private readSessionStorage(): string | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(CSRF_SESSION_STORAGE_KEY);
      return raw && raw.trim() !== '' ? raw.trim() : null;
    } catch {
      return null;
    }
  }

  private writeSessionStorage(token: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(CSRF_SESSION_STORAGE_KEY, token);
    } catch {
      // ignore
    }
  }

  private readCookieToken(): string | null {
    const encodedName = `${encodeURIComponent(CSRF_SESSION_STORAGE_KEY)}=`;

    for (const chunk of this.document.cookie.split(';')) {
      const cookie = chunk.trim();

      if (cookie.startsWith(encodedName)) {
        return decodeURIComponent(cookie.slice(encodedName.length));
      }
    }

    return null;
  }
}
